// The store dashboard, shown in place of the style grid: the day's sales (today
// unless another day is picked, up to a year back), the best-selling styles,
// sales by hour, and the open work as it stands now, with a way into the
// screen that deals with each. Online only: it adds up the server's bills.
// A range (last 7 days, this month) adds up the server's days; the sales by
// rep come from the bills saved on this till, and the daily target and the
// incentive % are kept on this till only.
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import db from "../../../database";
import { fashionPosService } from "../../../services";
import { formatMoney } from "../../../utils/fashion/money";
import { buildDashboardQuery, busiestHour, firstDashboardDay, hourLabel, readDashboard, todayIso, topStyles, validateDashboardDay, workRows } from "../../../utils/fashion/dashboard";
import {
  DASHBOARD_RANGES,
  NO_REP,
  configIncentiveRate,
  daysBetween,
  incentiveFor,
  mergeDashboards,
  rangeDays,
  readDailyTarget,
  readTillIncentiveRate,
  salesByRep,
  saveDailyTarget,
  saveTillIncentiveRate,
  targetProgress,
} from "../../../utils/fashion/salesRepReport";
import EmptyState from "./EmptyState";

const count = (v) => (v == null ? "—" : String(v));

// "Today", "Yesterday", or the short date ("Mon 19 Oct") for the day stepper.
export function dayLabel(day, today = todayIso()) {
  if (day === today) return "Today";
  if (day === dayjs(today).subtract(1, "day").format("YYYY-MM-DD")) return "Yesterday";
  return dayjs(day).format("ddd D MMM");
}

// Names the store floor uses for the open work (v4 board); the counts are the server's.
const BOARD_LABEL = {
  alterationsDue: "Alterations due, not ready",
  alterationsReady: "Alterations ready to hand over",
  followUpsDue: "Customer calls due today",
};
const workLabel = (r) => BOARD_LABEL[r.key] || r.label;

const shiftDay = (day, by) => dayjs(day).add(by, "day").format("YYYY-MM-DD");

// Days of a range, a few requests at a time, each read like a single day.
async function loadDays(bunitId, days) {
  const out = new Array(days.length);
  let next = 0;
  const worker = async () => {
    while (next < days.length) {
      const i = next++;
      const res = readDashboard(await fashionPosService(buildDashboardQuery(bunitId, days[i])));
      if (res.error) throw Object.assign(new Error(res.error), { fromServer: true });
      out[i] = res.dashboard;
    }
  };
  await Promise.all(Array.from({ length: Math.min(6, days.length) }, worker));
  return mergeDashboards(out);
}

// Bills saved on this till for the range, split by sales rep. Read with
// toArray and filtered here: works the same on IndexedDB and the CW Browser.
function useRepSales(from, to, reload, enabled) {
  const [state, setState] = useState({ rows: null, error: false });
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    (async () => {
      try {
        const [orders, reps] = await Promise.all([db.orders.toArray(), db.salesRep?.toArray ? db.salesRep.toArray().catch(() => []) : []]);
        const repNames = new Map((reps || []).map((r) => [r.s_salesrep_id, r.name]));
        if (live) setState({ rows: salesByRep(orders, { from, to, repNames }), error: false });
      } catch {
        if (live) setState({ rows: null, error: true });
      }
    })();
    return () => {
      live = false;
    };
  }, [from, to, reload, enabled]);
  return state;
}

function SettingField({ label, value, onSave, suffix, hint }) {
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  useEffect(() => setDraft(value == null ? "" : String(value)), [value]);
  return (
    <label className="fp-dash-set">
      <span className="fp-lbl fp-muted">{label}</span>
      <span className="fp-row">
        <input className="fp-dash-set-in fp-num" inputMode="decimal" value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => onSave(draft)} onKeyDown={(e) => e.key === "Enter" && onSave(draft)} aria-label={label} />
        {suffix && <span className="fp-muted">{suffix}</span>}
      </span>
      {hint && <span className="fp-dash-sub fp-muted">{hint}</span>}
    </label>
  );
}

export default function FashionDashboardPanel({ tillData, isOnline, posConfig, styles, productById, onOpen, onClose, backLabel = "Back to styles" }) {
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
  const online = typeof isOnline === "boolean" ? isOnline : typeof navigator === "undefined" || navigator.onLine;
  const [day, setDay] = useState(() => todayIso());
  // "day" walks one day at a time; "7d" and "month" add up their days.
  const [span, setSpan] = useState("day");
  const [view, setView] = useState({ status: "loading", dashboard: null, error: "" });
  const [reload, setReload] = useState(0);
  const [loadedAt, setLoadedAt] = useState(null);
  const storeName = tillData?.tillAccess?.csBunit?.name || "";
  const dayError = validateDashboardDay(day);
  const today = todayIso();
  const canBack = day > firstDashboardDay();
  const canNext = day < today;
  const { from, to } = span === "day" ? { from: day, to: day } : rangeDays(span);
  const rangeKey = span !== "day" ? span : day === today ? "today" : day === shiftDay(today, -1) ? "yesterday" : null;
  const tillId = tillData?.tillAccess?.cwrTill?.cwrTillID || tillData?.tillAccess?.cwrTill?.till || "";
  const repSales = useRepSales(from, to, reload, online);
  const configRate = configIncentiveRate(posConfig);
  const [tillRate, setTillRate] = useState(() => readTillIncentiveRate(tillId));
  const [dailyTarget, setDailyTarget] = useState(() => readDailyTarget(tillId));
  const [editing, setEditing] = useState(false);
  const rate = configRate ?? tillRate;
  const pickRange = (key) => {
    if (key === "today" || key === "yesterday") {
      setSpan("day");
      setDay(key === "today" ? today : shiftDay(today, -1));
    } else setSpan(key);
  };

  useEffect(() => {
    if (!online || !bunitId || dayError) return undefined;
    let live = true;
    setView((v) => ({ ...v, status: "loading" }));
    (async () => {
      let res;
      try {
        res =
          span === "day"
            ? readDashboard(await fashionPosService(buildDashboardQuery(bunitId, day)))
            : { dashboard: await loadDays(bunitId, daysBetween(from, to)) };
        if (!res.error && !res.dashboard) res = { error: "Couldn't load the dashboard. Check the connection and try again." };
      } catch (e) {
        res = { error: e?.fromServer ? e.message : "Couldn't load the dashboard. Check the connection and try again." };
      }
      if (!live) return;
      setView(res.error ? { status: "error", dashboard: null, error: res.error } : { status: "done", dashboard: res.dashboard, error: "" });
      if (!res.error) setLoadedAt(dayjs());
    })();
    return () => {
      live = false;
    };
  }, [online, bunitId, day, span, from, to, dayError, reload]);

  const styleById = useMemo(() => new Map((styles || []).map((s) => [s.styleId, s])), [styles]);
  const d = view.dashboard;
  const top = useMemo(() => (d ? topStyles(d.topProducts, productById, styleById) : []), [d, productById, styleById]);
  const money = (v) => (v == null ? "—" : formatMoney(v, tillData));
  const isToday = span === "day" && day === todayIso();
  const rangeDayCount = daysBetween(from, to).length;
  const rangeText = span === "day" ? dayLabel(day, today).toLowerCase() : DASHBOARD_RANGES.find((r) => r.key === span).label.toLowerCase();
  const progress = d ? targetProgress(d.netSales, dailyTarget, rangeDayCount) : null;
  const work = isToday ? workRows(d) : [];
  // Like the board: only what is waiting shows, busiest first; zero rows stay out of the way.
  const needs = work.filter((r) => r.count > 0).sort((a, b) => Number(Boolean(b.urgent)) - Number(Boolean(a.urgent)));
  const peak = d ? busiestHour(d.hours) : null;
  const maxHour = d ? Math.max(0, ...d.hours.map((h) => h.sales)) : 0;

  return (
    <section className="fp-panel" aria-label="Store dashboard">
      <div className="fp-panel-head">
        <h2 className="fp-panel-title">{isToday ? "Today in store" : "Store dashboard"}</h2>
        <div className="fp-row">
          {online && (
            <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => setReload((n) => n + 1)} disabled={view.status === "loading" || Boolean(dayError)}>
              Refresh
            </button>
          )}
          <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={onClose}>
            {backLabel}
          </button>
        </div>
      </div>
      {!online ? (
        <EmptyState icon="offline" title="You're offline" hint="The dashboard needs a connection, because it adds up the store's bills on the server. Reconnect the till and try again." />
      ) : (
        <>
          <div className="fp-row fp-dash-ranges">
            <div className="fp-dash-range" role="group" aria-label="Dashboard range">
              {DASHBOARD_RANGES.map((r) => (
                <button key={r.key} type="button" className={`fp-chip${rangeKey === r.key ? " is-on" : ""}`} aria-pressed={rangeKey === r.key} onClick={() => pickRange(r.key)}>
                  {r.label}
                </button>
              ))}
            </div>
            {span === "day" && (
              <div className="fp-dash-step" role="group" aria-label="Dashboard day">
                <button type="button" aria-label="Previous day" disabled={!canBack} onClick={() => setDay(shiftDay(day, -1))}>
                  ‹
                </button>
                <span className="fp-dash-step-l fp-num" aria-live="polite" title={day}>
                  {dayLabel(day, today)}
                </span>
                <button type="button" aria-label="Next day" disabled={!canNext} onClick={() => setDay(shiftDay(day, 1))}>
                  ›
                </button>
              </div>
            )}
          </div>
          {dayError ? (
            <p className="fp-line-msg is-err" role="alert">
              {dayError}
            </p>
          ) : view.status === "error" ? (
            <EmptyState icon="error" role="alert" title={view.error} action={{ label: "Try again", onClick: () => setReload((n) => n + 1) }} />
          ) : !d ? (
            <p className="fp-empty" role="status">
              Loading the dashboard…
            </p>
          ) : (
            <>
              <p className="fp-muted fp-num fp-dash-note">
                {[storeName, "all tills", from === to ? dayjs(day).format("ddd D MMM") : `${dayjs(from).format("D MMM")} – ${dayjs(to).format("D MMM")}`, loadedAt ? `updated ${loadedAt.format("HH:mm")}` : ""].filter(Boolean).join(" · ")}. Bills made offline appear once their till syncs.
              </p>
              <dl className="fp-dash-tiles" aria-label="Sales">
                <div>
                  <dt className="fp-lbl fp-muted">Net sales</dt>
                  <dd className="fp-num">{money(d.netSales)}</dd>
                  <dd className="fp-dash-sub fp-muted fp-num">
                    {d.returns > 0 ? `after ${d.returns} ${d.returns === 1 ? "return" : "returns"}, −${money(d.returnValue)}` : `${money(d.sales)} sold, no returns`}
                  </dd>
                </div>
                <div>
                  <dt className="fp-lbl fp-muted">Bills</dt>
                  <dd className="fp-num">{d.bills}</dd>
                  <dd className="fp-dash-sub fp-muted fp-num">avg {money(d.atv)}</dd>
                </div>
                <div>
                  <dt className="fp-lbl fp-muted">Pieces per bill</dt>
                  <dd className="fp-num">{d.upt == null ? "—" : d.upt}</dd>
                  <dd className="fp-dash-sub fp-muted fp-num">{d.units === 1 ? "1 piece sold" : `${d.units} pieces sold`}</dd>
                </div>
                <div>
                  <dt className="fp-lbl fp-muted">Online orders</dt>
                  <dd className="fp-num">
                    {count(d.onlineOrders)}
                    {d.onlineSales != null ? ` · ${money(d.onlineSales)}` : ""}
                  </dd>
                  <dd className="fp-dash-sub fp-muted">not in net sales</dd>
                </div>
              </dl>

              <div className="fp-dash-target" aria-label="Store target">
                {progress ? (
                  <>
                    <div className="fp-row fp-dash-target-h">
                      <span className="fp-lbl fp-muted">Store target {rangeDayCount > 1 ? `(${rangeDayCount} days)` : ""}</span>
                      <span className="fp-num">
                        {money(d.netSales)} of {money(progress.target)} · {progress.pct}%
                      </span>
                    </div>
                    <div className="fp-dash-bar fp-dash-target-bar" role="progressbar" aria-label="Net sales against target" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(100, progress.pct)}>
                      <span className={progress.met ? "is-met" : ""} style={{ width: `${Math.min(100, progress.pct)}%` }} />
                    </div>
                    <span className="fp-dash-sub fp-muted fp-num">
                      {progress.met ? "Target met." : `${money(progress.left)} to go.`} Set on this till, not from the back office.
                    </span>
                  </>
                ) : (
                  <span className="fp-dash-sub fp-muted">No daily target set for this till.</span>
                )}
                <button type="button" className="fp-btn-ghost fp-btn-sm" aria-expanded={editing} onClick={() => setEditing((v) => !v)}>
                  {editing ? "Done" : "Set target and incentive"}
                </button>
                {editing && (
                  <div className="fp-row fp-dash-sets">
                    <SettingField
                      label="Daily store target"
                      value={dailyTarget}
                      hint="Net sales a day. Kept on this till only."
                      onSave={(v) => {
                        saveDailyTarget(tillId, v);
                        setDailyTarget(readDailyTarget(tillId));
                      }}
                    />
                    {configRate == null ? (
                      <SettingField
                        label="Rep incentive"
                        value={tillRate}
                        suffix="% of net sales"
                        hint="For an estimate only. Kept on this till only."
                        onSave={(v) => {
                          saveTillIncentiveRate(tillId, v);
                          setTillRate(readTillIncentiveRate(tillId));
                        }}
                      />
                    ) : (
                      <span className="fp-dash-sub fp-muted fp-num">Rep incentive {configRate}% is set by the back office.</span>
                    )}
                  </div>
                )}
              </div>

              {isToday && (
                <>
                  <h3 className="fp-wp-h fp-dash-h">Needs you now</h3>
                  {work.length === 0 ? (
                    <p className="fp-muted">Couldn't read the open work. Refresh to try again.</p>
                  ) : needs.length === 0 ? (
                    <p className="fp-muted" role="status">
                      All clear. Nothing is waiting on the store.
                    </p>
                  ) : (
                    <ul className="fp-panel-list fp-dash-list" aria-label="Needs you now">
                      {needs.map((r) => (
                        <li key={r.key} className="fp-panel-row">
                          <span className={`fp-dash-n fp-num${r.urgent ? " is-err" : ""}`}>{count(r.count)}</span>
                          <span className="fp-panel-main">
                            <span className="fp-panel-doc">{workLabel(r)}</span>
                          </span>
                          {r.panel && onOpen ? (
                            <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => onOpen(r.panel)} aria-label={`Open ${workLabel(r).toLowerCase()}`}>
                              Open
                            </button>
                          ) : (
                            <span className="fp-dash-gap" />
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              <h3 className="fp-wp-h fp-dash-h">
                Best sellers {isToday ? "today" : rangeText} <span className="fp-num">({d.units} pieces sold)</span>
              </h3>
              {top.length === 0 ? (
                <EmptyState compact icon="empty" title="Nothing sold yet." />
              ) : (
                <table className="fp-dash-top" aria-label="Best sellers">
                  <thead>
                    <tr>
                      <th scope="col">Style</th>
                      <th scope="col" className="is-num">
                        Pieces
                      </th>
                      <th scope="col" className="is-num">
                        Net
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {top.map((s) => (
                      <tr key={s.styleId}>
                        <td>
                          <span className="fp-panel-doc">{s.name}</span>
                          {s.sizes.length > 1 && <span className="fp-wp-meta">{s.sizes.map((z) => `${z.label} ×${z.qty}`).join(" · ")}</span>}
                        </td>
                        <td className="is-num fp-num">{s.qty}</td>
                        <td className="is-num fp-num">{money(s.sales)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h3 className="fp-wp-h fp-dash-h">By sales rep, {rangeText}</h3>
              <p className="fp-muted fp-dash-sub">Bills saved on this till only. Returns take away from the rep's pieces and sales.{rate != null ? " Incentive is an estimate, not payroll." : ""}</p>
              {repSales.error ? (
                <p className="fp-muted">Couldn't read this till's bills.</p>
              ) : !repSales.rows ? (
                <p className="fp-empty" role="status">
                  Adding up this till's bills…
                </p>
              ) : repSales.rows.length === 0 ? (
                <EmptyState compact icon="empty" title="No bills on this till for these days." />
              ) : (
                <table className="fp-dash-top fp-dash-reps" aria-label="By sales rep">
                  <thead>
                    <tr>
                      <th scope="col">Rep</th>
                      <th scope="col" className="is-num">
                        Bills
                      </th>
                      <th scope="col" className="is-num">
                        Pieces
                      </th>
                      <th scope="col" className="is-num">
                        Net sales
                      </th>
                      <th scope="col" className="is-num">
                        Avg bill
                      </th>
                      {rate != null && (
                        <th scope="col" className="is-num">
                          Est. incentive ({rate}%)
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {repSales.rows.map((r) => (
                      <tr key={r.repId}>
                        <td>
                          <span className="fp-panel-doc">{r.name}</span>
                        </td>
                        <td className="is-num fp-num">{r.bills}</td>
                        <td className="is-num fp-num">{r.pieces}</td>
                        <td className="is-num fp-num">{money(r.sales)}</td>
                        <td className="is-num fp-num">{money(r.avgBill)}</td>
                        {rate != null && <td className="is-num fp-num">{r.repId === NO_REP ? "—" : money(incentiveFor(r.sales, rate))}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h3 className="fp-wp-h fp-dash-h">Bills by hour{peak ? ` (busiest ${hourLabel(peak.hour)})` : ""}</h3>
              {d.hours.length === 0 ? (
                <EmptyState compact icon="empty" title={span === "day" ? "No bills this day." : "No bills in these days."} />
              ) : (
                <ul className="fp-dash-hours" aria-label="Sales by hour">
                  {d.hours.map((h) => (
                    <li key={h.hour} className="fp-dash-hour">
                      <span className="fp-num fp-muted">{hourLabel(h.hour)}</span>
                      <span className="fp-dash-bar" aria-hidden="true">
                        <span style={{ width: `${maxHour > 0 ? Math.max(2, (h.sales / maxHour) * 100) : 0}%` }} />
                      </span>
                      <span className="fp-num">
                        {h.bills} · {money(h.sales)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
